/**
 * useFcmPush.js
 * ─────────────
 * Native push notifications for the Capacitor Android APK only.
 *   • Detects the Capacitor runtime — silent no-op in plain web (where
 *     the existing Web Push / VAPID flow in `usePushNotifications.js`
 *     handles browser pushes).
 *   • Requests permission, retrieves the FCM device token, and registers
 *     it with `POST /api/push/fcm/register` so the backend can deliver
 *     pushes via Firebase Cloud Messaging.
 *   • Re-emits foreground notifications as a `push:received` window event
 *     so any UI layer can show a toast.
 */
import { useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

let _registered = false;
let _listenersAttached = false;

/** Capacitor presence check that doesn't crash on plain web. */
function isNative() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * Idempotent FCM registration.
 * @param {object} opts
 * @param {string} [opts.appId='com.voiceride.app']
 * @param {(url:string)=>void} [opts.onNavigate]
 */
export async function registerFcmPush({ appId = 'com.voiceride.app', onNavigate } = {}) {
  if (!isNative() || _registered) return;

  let PushNotifications;
  try {
    PushNotifications = (await import('@capacitor/push-notifications')).PushNotifications;
  } catch {
    return; // plugin not bundled (web build)
  }

  // permission
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return;

  if (!_listenersAttached) {
    _listenersAttached = true;

    PushNotifications.addListener('registration', async (token) => {
      try {
        await axios.post(`${API}/push/fcm/register`, {
          token: token.value,
          platform: 'android',
          app_id: appId,
        });
        _registered = true;
      } catch (e) {
        console.warn('[fcm-push] register failed:', e?.response?.data || e?.message);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[fcm-push] registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      window.dispatchEvent(new CustomEvent('push:received', { detail: notification }));
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const url = action?.notification?.data?.url;
      if (url && typeof onNavigate === 'function') onNavigate(url);
    });
  }

  await PushNotifications.register();
}

/**
 * React hook — call once after login.
 *   useFcmPush({ enabled: !!user });
 */
export function useFcmPush({ enabled = true, appId, onNavigate } = {}) {
  useEffect(() => {
    if (!enabled) return;
    registerFcmPush({ appId, onNavigate }).catch(() => {});
  }, [enabled, appId, onNavigate]);
}

export default useFcmPush;
